import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { LoadingState } from "@/components/base/LoadingState";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { IntegrationsNav } from "@/components/feature/IntegrationsNav";
import { fmtDateTime } from "@/lib/format";
import {
  resolveIntegrationException,
  searchMappingTargets,
  type IntegrationExceptionRow,
  type MappingTargetRow,
} from "@/lib/integrations";
import { providerMeta } from "@/lib/integrations/registry";
import { useBulkSelection } from "@/components/feature/useBulkSelection";
import { BulkActionBar } from "@/components/feature/BulkActionBar";
import {
  exceptionInboxStatus,
  exceptionPrimaryRef,
  exceptionTypeLabel,
  mergeExternalOrderIds,
} from "@/lib/operational-issues";
import { channelDisplayLine } from "@/lib/sync-events-ux";
import { unmappedItemFallback } from "@/lib/operational-issue-ux";
import {
  extractMarketplaceItemIdentity,
  mappingSuggestionKind,
  mappingSuggestionLabel,
} from "@/lib/marketplace-identity";

const filters = [
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
  { value: "all", label: "All" },
];

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

export default function IntegrationExceptions() {
  const { org, role } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const skuParam = (searchParams.get("sku") ?? "").trim();
  const isAdmin = role === "owner" || role === "admin";
  const [filter, setFilter] = useState("open");
  const [mapTarget, setMapTarget] = useState<IntegrationExceptionRow | null>(null);
  const [selectedKey, setSelectedKey] = useState("");
  const [mapQuery, setMapQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [bulkMsg, setBulkMsg] = useState("");

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(mapQuery.trim()), 200);
    return () => window.clearTimeout(t);
  }, [mapQuery]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["integration-exceptions", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      await supabase.rpc("reconcile_integration_exceptions", { p_org_id: org!.id });
      const [exceptions, connectors] = await Promise.all([
        supabase
          .from("integration_exceptions")
          .select("id, connector_id, exception_type, entity_type, entity_ref, external_data, status, resolution_note, resolved_at, created_at, first_seen, last_seen, occurrence_count, issue_fingerprint, superseded_by")
          .eq("org_id", org!.id)
          .is("superseded_by", null)
          .order("last_seen", { ascending: false })
          .limit(200),
        supabase.from("connectors").select("id, name, provider").eq("org_id", org!.id),
      ]);
      const rows = (exceptions.data ?? []) as IntegrationExceptionRow[];
      const refundIds = rows
        .filter((e) => e.entity_type === "sales_refund" && e.entity_ref)
        .map((e) => e.entity_ref as string);
      let refunds: { id: string; provider_refund_id: string | null; sales_order_id: string | null }[] = [];
      let orders: { id: string; order_number: string }[] = [];
      if (refundIds.length) {
        const refundRes = await supabase
          .from("sales_refunds")
          .select("id, provider_refund_id, sales_order_id")
          .eq("org_id", org!.id)
          .in("id", refundIds);
        refunds = (refundRes.data ?? []) as typeof refunds;
        const soIds = refunds.map((r) => r.sales_order_id).filter((id): id is string => Boolean(id));
        if (soIds.length) {
          const soRes = await supabase.from("sales_orders").select("id, order_number").eq("org_id", org!.id).in("id", soIds);
          orders = (soRes.data ?? []) as typeof orders;
        }
      }
      return {
        exceptions: rows,
        connectors: (connectors.data ?? []) as { id: string; name: string; provider: string }[],
        refunds,
        orders,
      };
    },
  });

  const mapIdentity = useMemo(
    () =>
      mapTarget
        ? extractMarketplaceItemIdentity({ entityRef: mapTarget.entity_ref, externalData: mapTarget.external_data })
        : null,
    [mapTarget],
  );

  const { data: targets = [], isFetching: searching } = useQuery({
    queryKey: ["mapping-targets", org?.id, debouncedQuery],
    enabled: !!org?.id && !!mapTarget && debouncedQuery.length > 0,
    queryFn: () => searchMappingTargets(org!.id, debouncedQuery),
  });

  const connectorById: Record<string, { name: string; provider: string }> = {};
  for (const c of data?.connectors ?? []) connectorById[c.id] = c;

  const refundById: Record<string, { provider_refund_id: string | null; sales_order_id: string | null }> = {};
  for (const r of data?.refunds ?? []) refundById[r.id] = r;
  const orderNumberById: Record<string, string> = {};
  for (const o of data?.orders ?? []) orderNumberById[o.id] = o.order_number;

  const filtered = (data?.exceptions ?? []).filter((e) => {
    if (!exceptionInboxStatus(filter as "open" | "resolved" | "all", e.status)) return false;
    if (skuParam && (e.entity_ref ?? "") !== skuParam) return false;
    return true;
  });

  const openCount = (data?.exceptions ?? []).filter((e) => exceptionInboxStatus("open", e.status)).length;

  const bulk = useBulkSelection<IntegrationExceptionRow>(filtered);

  const selectedTarget: MappingTargetRow | undefined = targets.find((t) => `${t.product_id}:${t.variant_id ?? ""}` === selectedKey);

  const openMap = (e: IntegrationExceptionRow) => {
    const item = extractMarketplaceItemIdentity({ entityRef: e.entity_ref, externalData: e.external_data });
    setError("");
    setSelectedKey("");
    setMapTarget(e);
    setMapQuery(item.productName || "");
    setDebouncedQuery(item.productName || "");
  };

  const confirmMap = async () => {
    if (!mapTarget || !selectedTarget) return;
    setError("");
    setSaving(true);
    try {
      const entityType = selectedTarget.variant_id ? "variant" : "product";
      const entityId = selectedTarget.variant_id || selectedTarget.product_id;
      const res = await resolveIntegrationException({
        exceptionId: mapTarget.id,
        action: "map",
        entityType,
        entityId,
        note: `Mapped by operator to ${selectedTarget.product_name}${selectedTarget.variant_label ? ` · ${selectedTarget.variant_label}` : ""} (${selectedTarget.variant_sku || selectedTarget.product_sku || ""})`,
      });
      if (!res.ok) setError(res.error ?? "Unable to resolve.");
      else {
        setMapTarget(null);
        setSelectedKey("");
        queryClient.invalidateQueries({ queryKey: ["integration-exceptions", org?.id] });
      }
    } catch (err) {
      setError((err as Error).message);
    }
    setSaving(false);
  };

  const ignore = async (e: IntegrationExceptionRow) => {
    setSaving(true);
    await resolveIntegrationException({ exceptionId: e.id, action: "ignore", note: "Ignored by administrator" });
    setSaving(false);
    queryClient.invalidateQueries({ queryKey: ["integration-exceptions", org?.id] });
  };

  const bulkIgnore = async () => {
    setSaving(true);
    setBulkMsg("");
    let ok = 0;
    let failed = 0;
    for (const exId of bulk.selectedIds) {
      try {
        const res = await resolveIntegrationException({ exceptionId: exId, action: "ignore", note: "Acknowledged in bulk" });
        if (res.ok) ok++;
        else failed++;
      } catch {
        failed++;
      }
    }
    setSaving(false);
    setBulkMsg(`${ok} acknowledged${failed ? `, ${failed} failed` : ""}.`);
    bulk.clear();
    queryClient.invalidateQueries({ queryKey: ["integration-exceptions", org?.id] });
  };

  const mapConn = mapTarget?.connector_id ? connectorById[mapTarget.connector_id] : undefined;
  const mapMeta = mapConn ? providerMeta(mapConn.provider) : undefined;

  return (
    <div>
      <PageHeader
        title="Integration Exceptions"
        subtitle="Current mapping and sync problems that still need an operator. Resolved history stays under Resolved / All."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1">
                {filters.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setFilter(f.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                      filter === f.value ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {f.label}
                    {f.value === "open" && openCount > 0 ? ` (${openCount})` : ""}
                  </button>
                ))}
              </div>
            </div>

            {isLoading ? (
              <div className="p-8">
                <LoadingState />
              </div>
            ) : isError ? (
              <div className="p-8 text-center">
                <p className="text-sm text-slate-500 mb-4">Couldn't load exceptions.</p>
                <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">
                  Retry
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-6">
                <EmptyState
                  icon="ri-check-double-line"
                  title={filter === "open" ? "No open exceptions" : "Nothing here"}
                  description="When a marketplace item can't be mapped or synced, it appears here for resolution."
                />
              </div>
            ) : (
              <>
                {bulk.someSelected && isAdmin && (
                  <div className="px-4 py-3 border-b border-slate-100 bg-emerald-50/50">
                    <BulkActionBar count={bulk.selected.size} onClear={bulk.clear}>
                      <button onClick={bulkIgnore} disabled={saving} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap">
                        Acknowledge selected
                      </button>
                    </BulkActionBar>
                  </div>
                )}
                {bulkMsg && <div className="px-4 py-2.5 text-sm text-slate-600 border-b border-slate-100">{bulkMsg}</div>}
                <div className="divide-y divide-slate-50">
                {filtered.map((e) => {
                  const conn = e.connector_id ? connectorById[e.connector_id] : undefined;
                  const meta = conn ? providerMeta(conn.provider) : undefined;
                  const item = extractMarketplaceItemIdentity({ entityRef: e.entity_ref, externalData: e.external_data });
                  const refund = e.entity_ref ? refundById[e.entity_ref] : undefined;
                  const primary = exceptionPrimaryRef({
                    exceptionType: e.exception_type,
                    entityRef: e.entity_ref,
                    providerRefundId: refund?.provider_refund_id,
                    providerLabel: meta?.label,
                  });
                  const orderLabel = refund?.sales_order_id ? orderNumberById[refund.sales_order_id] : item.externalOrderId;
                  const lastSeen = e.last_seen ?? e.created_at;
                  const occurrences = e.occurrence_count ?? 1;
                  const actionable = exceptionInboxStatus("open", e.status);
                  const badgeStatus = (["open", "acknowledged", "resolved", "ignored"].includes(e.status)
                    ? e.status
                    : "open") as "open" | "acknowledged" | "resolved" | "ignored";
                  const unmapped = e.exception_type === "unmapped_product";
                  return (
                    <div key={e.id} className="px-4 py-4 flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        {isAdmin && actionable && (
                          <input type="checkbox" checked={bulk.selected.has(e.id)} onChange={() => bulk.toggle(e.id)} className="mt-1 w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer shrink-0" />
                        )}
                        <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                          <i className="ri-alert-line text-amber-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-slate-800">
                              {exceptionTypeLabel(e.exception_type)}
                            </span>
                            <StatusBadge status={badgeStatus} />
                          </div>
                          {unmapped ? (
                            <>
                              <p className="text-sm text-slate-800 mt-1">
                                {item.productName
                                  || unmappedItemFallback(channelDisplayLine({
                                    id: e.connector_id ?? "",
                                    name: conn?.name ?? meta?.label ?? "channel",
                                    provider: conn?.provider ?? null,
                                  }))}
                              </p>
                              {item.variantLabel && <p className="text-xs text-slate-600 mt-0.5">{item.variantLabel}</p>}
                              {item.sku && (
                                <p className="text-xs text-slate-500 mt-0.5">Incoming SKU: {item.sku}</p>
                              )}
                            </>
                          ) : (
                            <p className="text-xs text-slate-500 mt-1">
                              {primary || "No reference"}
                              {orderLabel ? ` · order ${orderLabel}` : ""}
                            </p>
                          )}
                          <p className="text-xs text-slate-400 mt-0.5">
                            {channelDisplayLine({
                              id: e.connector_id ?? "",
                              name: conn?.name ?? meta?.label ?? "channel",
                              provider: conn?.provider ?? null,
                            })}
                            {` · last seen ${fmtDateTime(lastSeen)}`}
                            {occurrences > 1 ? ` · ${occurrences} observations` : ""}
                            {unmapped && item.externalOrderId ? ` · order ${item.externalOrderId}` : ""}
                          </p>
                          {e.resolution_note && <p className="text-xs text-slate-400 mt-0.5 italic">{e.resolution_note}</p>}
                          <details className="mt-1">
                            <summary className="text-[11px] text-slate-400 cursor-pointer">Details</summary>
                            <p className="text-[11px] text-slate-400 font-mono break-all mt-0.5">
                              issue {e.id}
                              {e.issue_fingerprint ? ` · fingerprint ${e.issue_fingerprint}` : ""}
                              {e.entity_type ? ` · entity_type ${e.entity_type}` : ""}
                              {e.entity_ref ? ` · incoming SKU ${e.entity_ref}` : ""}
                              {e.connector_id ? ` · connector_id ${e.connector_id}` : ""}
                            </p>
                          </details>
                        </div>
                      </div>
                      {isAdmin && actionable && (
                        <div className="flex items-center gap-1 shrink-0">
                          {unmapped && (
                            <button
                              onClick={() => openMap(e)}
                              className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium rounded-lg whitespace-nowrap"
                            >
                              Map to product
                            </button>
                          )}
                          <button
                            onClick={() => ignore(e)}
                            disabled={saving}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-500 hover:bg-slate-100 whitespace-nowrap"
                          >
                            Ignore
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <IntegrationsNav />
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-800 mb-2">About exceptions</h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              Marketplace names come from the provider order line, never from a guessed Tradivo title.
              Mapping is connector-scoped and requires an explicit product/variant choice.
              {skuParam ? ` Showing incoming SKU ${skuParam}.` : ""}
            </p>
          </div>
        </div>
      </div>

      <Modal
        open={!!mapTarget}
        onClose={() => { setMapTarget(null); setSelectedKey(""); }}
        title="Map marketplace item"
        size="lg"
        scrollBody
        footer={
          <>
            <button
              onClick={() => { setMapTarget(null); setSelectedKey(""); }}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={confirmMap}
              disabled={saving || !selectedTarget}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {saving ? "Mapping…" : "Map & resolve"}
            </button>
          </>
        }
      >
        <div className="space-y-5">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">External item</p>
            <p className="text-xs text-slate-500">
              Provider item name: <span className="font-medium text-slate-800">{mapIdentity?.productName || "Not provided by marketplace"}</span>
            </p>
            <p className="text-xs text-slate-500">
              Incoming SKU: <span className="font-mono font-medium text-slate-800">{mapIdentity?.sku || mapTarget?.entity_ref}</span>
            </p>
            <p className="text-xs text-slate-500">
              Order number(s): <span className="font-medium text-slate-800">
                {mergeExternalOrderIds(mapTarget?.external_data?.external_order_ids, mapTarget?.external_data?.external_order_id).join(", ") || mapIdentity?.externalOrderId || "—"}
              </span>
            </p>
            <p className="text-xs text-slate-500">
              Quantity / observations: <span className="font-medium text-slate-800">
                {mapIdentity?.quantity ?? "—"}
                {mapTarget?.occurrence_count && mapTarget.occurrence_count > 1 ? ` · ${mapTarget.occurrence_count} observations` : ""}
              </span>
            </p>
            <p className="text-xs text-slate-500">
              Channel: <span className="font-medium text-slate-800">{mapMeta?.label ?? "Channel"}{mapConn ? ` · ${mapConn.name}` : ""}</span>
            </p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Map to Tradivo</p>
            <label className={labelCls}>Search (product name, SKU, barcode, GTIN, reference)</label>
            <input
              value={mapQuery}
              onChange={(e) => { setMapQuery(e.target.value); setSelectedKey(""); }}
              className={inputCls}
              placeholder="Search catalogue…"
            />
          </div>
          {searching && <p className="text-xs text-slate-400">Searching…</p>}
          {!searching && debouncedQuery && targets.length === 0 && (
            <p className="text-xs text-slate-500">No catalogue matches. Name similarity never maps automatically.</p>
          )}
          {targets.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-600">Possible matches</p>
              <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
                {targets.map((t) => {
                  const key = `${t.product_id}:${t.variant_id ?? ""}`;
                  const kind = mappingSuggestionKind({
                    externalSku: mapIdentity?.sku,
                    candidateSku: t.variant_sku || t.product_sku,
                    externalProductName: mapIdentity?.productName,
                    candidateProductName: t.product_name,
                    barcodeOrGtinMatch: t.matched_field === "barcode" || ["gtin", "upc", "ean", "isbn"].includes(t.matched_field),
                  });
                  const badge = mappingSuggestionLabel(kind);
                  return (
                    <label key={key} className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer ${selectedKey === key ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
                      <input
                        type="radio"
                        name="mapping-target"
                        className="mt-1"
                        checked={selectedKey === key}
                        onChange={() => setSelectedKey(key)}
                      />
                      <div>
                        <p className="text-sm font-medium text-slate-800">{t.product_name}</p>
                        <p className="text-xs text-slate-500">
                          {t.variant_label || "Default variant"}
                          {t.variant_sku ? ` · ${t.variant_sku}` : t.product_sku ? ` · ${t.product_sku}` : ""}
                          {` · ${t.status}`}
                        </p>
                        {badge && <p className="text-[11px] text-slate-400 mt-0.5">{badge} · suggestion only</p>}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
